import React from 'react';
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import '../OrderInvoice/registerInvoiceFonts';

/**
 * The raw-fabric sales invoice — one A4 page per sale.
 *
 * Built to the "Fabric Invoice A4" design. Three things about a fabric
 * consignment drive the layout: weight and money must never be mistaken for one
 * another, rolls can legitimately be zero where cut lengths were supplied, and
 * the amount still owed is the number the buyer and the accounts desk both look
 * for first — so it sits in an inverted block, the only filled area on the page.
 *
 * It prints in black and white as often as it is read on screen, so nothing is
 * distinguished by colour alone: emphasis is weight, rule thickness and inversion.
 */

const C = {
  text: '#16181a',
  body: '#3d4144',
  muted: '#5a5f63',
  line: '#dcdfe1',
  row: '#e6e8ea',
  faint: '#eef0f1',
  ghost: '#c4c8cb',
  onDark: '#e9ebec',
  /* Rule inside the inverted due block. A hex, not rgba: react-pdf does not
     blend it against the black behind it the way a browser would. */
  onDarkRule: '#6f7477',
};

/** Rows the table is padded out to, so a two-line sale still reads as a table. */
const MIN_ROWS = 9;

const s = StyleSheet.create({
  // Helvetica is built into react-pdf; Courier Prime and Hind Siliguri are
  // registered by the order invoice and shared from there.
  page: { fontFamily: 'Helvetica', color: C.text, fontSize: 9.5, lineHeight: 1.35, paddingTop: 40, paddingHorizontal: 42, paddingBottom: 34 },
  /** Figures: monospaced so columns of numbers line up and can be checked by hand. */
  mono: { fontFamily: 'CourierPrime' },
  /**
   * Anything that can carry Bangla — customer names, addresses, notes — plus the
   * ৳ sign itself. Neither Helvetica nor Courier Prime has a Bengali glyph, and
   * react-pdf does not fall back across families for a missing one.
   */
  bn: { fontFamily: 'HindSiliguri' },

  eyebrow: { fontSize: 7, letterSpacing: 2, textTransform: 'uppercase', color: C.muted, fontWeight: 700 },
  metaLabel: { fontSize: 7.5, letterSpacing: 0.6, textTransform: 'uppercase', color: C.muted, fontWeight: 700 },

  th: { fontSize: 7, letterSpacing: 1.2, textTransform: 'uppercase', color: C.muted, paddingBottom: 5 },
  td: { paddingVertical: 3.2 },

  rule: { height: 2.5, backgroundColor: C.text, marginTop: 10 },
  sign: { flex: 1, borderTopWidth: 1, borderTopColor: C.text, paddingTop: 5 },
});

/** Widths when every line is priced, and when none is. Percentages of the text column. */
const COLS = {
  priced: { n: '4%', type: '31%', colour: '14%', kg: '14%', rolls: '8%', rate: '13%', amount: '16%' },
  plain: { n: '5%', type: '44%', colour: '20%', kg: '18%', rolls: '13%' },
};

const num = (v, d = 2) =>
  Number(v ?? 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

const fmtDate = (d) => (d ? dayjs(d).format('D MMM YYYY') : '—');

/** ৳ then the figure, each in the family that can actually draw it. */
const Taka = ({ children, style }) => (
  <Text style={style}>
    <Text style={s.bn}>৳ </Text>
    <Text style={s.mono}>{children}</Text>
  </Text>
);

const FabricInvoice = ({ sale, seller }) => {
  const lines = sale?.lines || [];
  // The rate columns appear only when every line carries one. A table where
  // some rows show their working and others do not reads as an error, and a
  // column of amounts that does not sum to the total reads as a worse one.
  const priced = lines.length > 0 && lines.every((l) => l.rate != null);
  const col = priced ? COLS.priced : COLS.plain;

  const amount = Number(sale?.amount ?? 0);
  // Money is allocated across the buyer's whole account, oldest bill first, so
  // these come from the ledger rather than being subtracted here. `applied` is
  // what settled THIS consignment; `previous` is what was still open on older
  // ones; `balanceDue` is what the buyer owes when both are taken together.
  const applied = Number(sale?.paidAmount ?? 0);
  const previous = Number(sale?.previousBalance ?? 0);
  const balanceDue = Number(sale?.balanceDue ?? previous + amount - applied);
  const advance = Number(sale?.advance ?? 0);
  const settled = balanceDue < 0.005;

  const addr = sale?.customer?.address;
  const fullAddress = addr
    ? [addr.street, addr.upazila, addr.district, addr.division].filter(Boolean).join(', ')
    : '';
  const contact = [addr?.phone || sale?.customer?.phone, sale?.customer?.altPhone].filter(Boolean).join(' · ');

  const fillers = [];
  for (let i = lines.length; i < MIN_ROWS; i += 1) fillers.push(i + 1);

  const pad = (i) => String(i).padStart(2, '0');

  return (
    <Document>
      <Page size="A4" style={s.page}>

        {/* Seller / invoice meta */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <View style={{ flex: 1, paddingRight: 40 }}>
            <Text style={{ fontSize: 19, fontWeight: 700, letterSpacing: 1.2 }}>{seller.name}</Text>
            <Text style={[s.eyebrow, { marginTop: 3, letterSpacing: 2.4, fontSize: 7.5 }]}>{seller.tagline}</Text>
            <View style={{ marginTop: 7 }}>
              {seller.lines.map((l, i) => (
                <Text key={i} style={{ fontSize: 8.5, color: C.body, lineHeight: 1.5 }}>{l}</Text>
              ))}
            </View>
          </View>

          <View style={{ width: 210 }}>
            <Text style={{ fontSize: 15, fontWeight: 700, letterSpacing: 3, textAlign: 'right' }}>INVOICE</Text>
            <View style={{ marginTop: 8 }}>
              <Meta label="Invoice No." value={sale?.invoiceNo || '—'} strong />
              <Meta label="Sale Date" value={fmtDate(sale?.happenedOn)} divider />
              <Meta label="Payment Method" value={sale?.paymentMethod || 'Unrecorded'} divider plain />
            </View>
          </View>
        </View>

        <View style={s.rule} />

        {/* Buyer / consignment summary */}
        <View style={{ flexDirection: 'row', marginTop: 9, alignItems: 'stretch' }}>
          <View style={{ flex: 1, marginRight: 28, borderBottomWidth: 1, borderBottomColor: C.line, paddingBottom: 8 }}>
            <Text style={s.eyebrow}>Billed to</Text>
            <Text style={[s.bn, { fontSize: 11, fontWeight: 700, marginTop: 5 }]}>{sale?.customer?.name || '—'}</Text>
            {!!contact && <Text style={{ fontSize: 8.5, color: C.body, marginTop: 2 }}>{contact}</Text>}
            {!!fullAddress && <Text style={[s.bn, { fontSize: 8.5, color: C.body, lineHeight: 1.5 }]}>{fullAddress}</Text>}
          </View>

          <View style={{ width: 158, borderWidth: 1, borderColor: C.text, paddingVertical: 7, paddingHorizontal: 9, justifyContent: 'center' }}>
            <Text style={s.eyebrow}>Consignment</Text>
            <Fact label="Lines" value={`${lines.length} ${lines.length === 1 ? 'line' : 'lines'}`} first />
            <Fact label="Total rolls" value={String(sale?.totalRolls ?? 0)} />
            <Fact label="Total weight" value={`${num(sale?.totalWeight)} kg`} />
          </View>
        </View>

        {/* Lines */}
        <View style={{ marginTop: 11 }}>
          <View style={{ flexDirection: 'row', borderBottomWidth: 1.5, borderBottomColor: C.text }}>
            <Text style={[s.th, { width: col.n }]}>#</Text>
            <Text style={[s.th, { width: col.type }]}>Fabric type</Text>
            <Text style={[s.th, { width: col.colour }]}>Colour</Text>
            <Text style={[s.th, { width: col.kg, textAlign: 'right', paddingHorizontal: 8, borderLeftWidth: 1, borderLeftColor: C.line }]}>Weight</Text>
            <Text style={[s.th, { width: col.rolls, textAlign: 'right', paddingHorizontal: 8 }]}>Rolls</Text>
            {priced && (
              <>
                <Text style={[s.th, { width: col.rate, textAlign: 'right', paddingLeft: 10, paddingRight: 8, borderLeftWidth: 1, borderLeftColor: C.text }]}>
                  Rate <Text style={s.bn}>৳</Text>/kg
                </Text>
                <Text style={[s.th, { width: col.amount, textAlign: 'right', paddingLeft: 10 }]}>
                  Amount <Text style={s.bn}>৳</Text>
                </Text>
              </>
            )}
          </View>

          {lines.map((l, i) => (
            <View key={l.id || i} wrap={false} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: C.row }}>
              <Text style={[s.td, s.mono, { width: col.n, fontSize: 8, color: C.muted }]}>{pad(i + 1)}</Text>
              <Text style={[s.td, s.bn, { width: col.type, fontWeight: 700, paddingRight: 6 }]}>{l.fabricType || '—'}</Text>
              <Text style={[s.td, s.bn, { width: col.colour, fontSize: 9, color: C.body, paddingRight: 6 }]}>{l.fabricColor || '—'}</Text>
              <Text style={[s.td, { width: col.kg, textAlign: 'right', paddingHorizontal: 8, borderLeftWidth: 1, borderLeftColor: C.row }]}>
                <Text style={[s.mono, { fontSize: 9 }]}>{num(l.weight)}</Text>
                <Text style={{ fontSize: 7, color: C.muted }}> kg</Text>
              </Text>
              <Text style={[s.td, s.mono, { width: col.rolls, textAlign: 'right', paddingHorizontal: 8, fontSize: 9, color: C.body }]}>{l.rolls ?? 0}</Text>
              {priced && (
                <>
                  <Text style={[s.td, s.mono, { width: col.rate, textAlign: 'right', paddingLeft: 10, paddingRight: 8, fontSize: 9, borderLeftWidth: 1, borderLeftColor: C.text }]}>{num(l.rate)}</Text>
                  <Text style={[s.td, s.mono, { width: col.amount, textAlign: 'right', paddingLeft: 10, fontWeight: 700 }]}>{num(l.lineAmount)}</Text>
                </>
              )}
            </View>
          ))}

          {/* Ruled-off empty rows: the table keeps its shape, and nothing can be
              added under the last line after the invoice is handed over. */}
          {fillers.map((n) => (
            <View key={`f${n}`} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: C.faint }}>
              <Text style={[s.td, s.mono, { width: col.n, fontSize: 8, color: C.ghost }]}>{pad(n)}</Text>
              <Text style={[s.td, { width: col.type }]}> </Text>
              <Text style={[s.td, { width: col.colour }]}> </Text>
              <Text style={[s.td, { width: col.kg, borderLeftWidth: 1, borderLeftColor: C.faint }]}> </Text>
              <Text style={[s.td, { width: col.rolls }]}> </Text>
              {priced && (
                <>
                  <Text style={[s.td, { width: col.rate, borderLeftWidth: 1, borderLeftColor: C.line }]}> </Text>
                  <Text style={[s.td, { width: col.amount }]}> </Text>
                </>
              )}
            </View>
          ))}

          {/* Consignment total */}
          <View style={{ flexDirection: 'row', borderTopWidth: 1.5, borderTopColor: C.text }}>
            <Text style={{ width: `${parseFloat(col.n) + parseFloat(col.type) + parseFloat(col.colour)}%`, paddingVertical: 7, fontSize: 7.5, letterSpacing: 1.4, textTransform: 'uppercase', fontWeight: 700 }}>
              Consignment total
            </Text>
            <Text style={{ width: col.kg, paddingVertical: 7, paddingHorizontal: 8, textAlign: 'right', borderLeftWidth: 1, borderLeftColor: C.row }}>
              <Text style={[s.mono, { fontWeight: 700 }]}>{num(sale?.totalWeight)}</Text>
              <Text style={{ fontSize: 7, color: C.muted }}> kg</Text>
            </Text>
            <Text style={[s.mono, { width: col.rolls, paddingVertical: 7, paddingHorizontal: 8, textAlign: 'right', fontWeight: 700 }]}>{sale?.totalRolls ?? 0}</Text>
            {priced && (
              <>
                <Text style={{ width: col.rate, borderLeftWidth: 1, borderLeftColor: C.text }}> </Text>
                <Text style={[s.mono, { width: col.amount, paddingVertical: 7, paddingLeft: 10, textAlign: 'right', fontSize: 10, fontWeight: 700 }]}>{num(amount)}</Text>
              </>
            )}
          </View>
        </View>

        {/* Pushes the money and the signatures to the foot of the page. */}
        <View style={{ flexGrow: 1 }} />

        <View wrap={false} style={{ flexDirection: 'row', alignItems: 'flex-end', marginTop: 8 }}>
          <View style={{ flex: 1, marginRight: 28 }}>
            <Text style={[s.eyebrow, { marginBottom: 4 }]}>Notes</Text>
            {!!sale?.note && <Text style={[s.bn, { fontSize: 8, color: C.body, lineHeight: 1.6, marginBottom: 4 }]}>{sale.note}</Text>}
            <Text style={{ fontSize: 8, color: C.body, lineHeight: 1.6 }}>{seller.terms}</Text>
          </View>

          <View style={{ width: 215 }}>
            {/* A running account, not a standalone bill. The buyer pays in
                lumps against everything open, so the invoice has to show him
                what he was already carrying — otherwise a payment that cleared
                an older consignment looks like it went missing. */}
            <Row label="Previous balance" value={num(previous)} />
            <Row label="This invoice" value={num(amount)} divider />
            <Row label="Payments applied" value={num(applied)} divider />

            {/* The one filled block on the page — and it inverts when money is
                still owed, so an unsettled invoice is obvious face down on a desk. */}
            <View style={{ marginTop: 8, borderWidth: 2.5, borderColor: C.text, paddingVertical: 8, paddingHorizontal: 11, backgroundColor: settled ? '#ffffff' : C.text }}>
              {/* Label above the figure rather than beside it: a seven-figure
                  due in 17pt monospace is wider than what a justified row
                  leaves it, and react-pdf overlaps rather than reflows. */}
              <Text style={{ fontSize: 7.5, letterSpacing: 2.2, textTransform: 'uppercase', fontWeight: 700, color: settled ? C.text : '#ffffff' }}>Balance due</Text>
              <Taka style={{ fontSize: 17, fontWeight: 700, textAlign: 'right', color: settled ? C.text : '#ffffff' }}>{num(settled ? 0 : balanceDue)}</Taka>
              <Text
                style={{
                  marginTop: 4, paddingTop: 4, fontSize: 7.5, textTransform: 'uppercase', fontWeight: 700,
                  borderTopWidth: 1, borderTopColor: settled ? C.text : C.onDarkRule,
                  color: settled ? C.text : C.onDark,
                  letterSpacing: settled ? 2.6 : 1.4,
                  textAlign: settled ? 'center' : 'left',
                }}
              >
                {settled ? 'Settled in full' : 'Payable on receipt'}
              </Text>
            </View>

            {/* Credit is not the same as a settled account: the buyer has money
                with us that his next consignment will draw on, and he should be
                able to see it. Only shown when there is any. */}
            {advance >= 0.005 && (
              <View style={{ marginTop: 6, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={{ fontSize: 8, color: C.body }}>Advance held on account</Text>
                <Taka style={{ fontSize: 9 }}>{num(advance)}</Taka>
              </View>
            )}
          </View>
        </View>

        <View wrap={false} style={{ flexDirection: 'row', marginTop: 16 }}>
          <View style={[s.sign, { marginRight: 40 }]}>
            <Text style={{ fontSize: 8, fontWeight: 700 }}>Authorised signatory</Text>
            <Text style={{ fontSize: 7.5, color: C.muted, marginTop: 1 }}>{seller.name} · Name, designation &amp; date</Text>
          </View>
          <View style={s.sign}>
            <Text style={{ fontSize: 8, fontWeight: 700 }}>Received in good order</Text>
            <Text style={{ fontSize: 7.5, color: C.muted, marginTop: 1 }}>Buyer or agent · Name &amp; date</Text>
          </View>
        </View>

        <Text style={{ marginTop: 7, fontSize: 7, color: C.muted, letterSpacing: 0.4 }}>
          {seller.name} · {sale?.invoiceNo || '—'} · Computer generated invoice, valid without seal.
        </Text>

      </Page>
    </Document>
  );
};

const Meta = ({ label, value, strong, divider, plain }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 3.5, borderTopWidth: divider ? 1 : 0, borderTopColor: C.line }}>
    <Text style={s.metaLabel}>{label}</Text>
    <Text style={[plain ? null : s.mono, { fontSize: strong ? 9.5 : 8.5, fontWeight: strong || plain ? 700 : 400 }]}>{value}</Text>
  </View>
);

const Fact = ({ label, value, first }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: first ? 5 : 3 }}>
    <Text style={{ fontSize: 8.5, color: C.body }}>{label}</Text>
    <Text style={[s.mono, { fontSize: 8.5, fontWeight: 700 }]}>{value}</Text>
  </View>
);

const Row = ({ label, value, divider }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 4, borderTopWidth: divider ? 1 : 0, borderTopColor: C.line }}>
    <Text style={{ fontSize: 9, color: C.body }}>{label}</Text>
    <Taka style={{ fontSize: 9.5 }}>{value}</Taka>
  </View>
);

export default FabricInvoice;
